import React, { useEffect, useState } from 'react';
import { Search, Filter, Briefcase, X } from 'lucide-react';
import SEO from '../components/SEO';
import { useLocation, useNavigate } from 'react-router-dom';
import CompanyLogo from '../components/CompanyLogo';
import NewsletterSubscription from '../components/NewsletterSubscription';
import { useJobsContext } from '../contexts/JobsSearchContext';
import '@fortawesome/fontawesome-free/css/all.min.css';
import AnimatedHand from '../components/AnimatedHand';
import FilterToggleButton from '../components/FilterToggleButton';
import SkillsList from '../components/SkillsList';
import ReactGA from 'react-ga4';
import companiesData from '../data/companies.json';
import jobsData from '../data/jobs/jobs-1.json';

export interface CompanyWithJobPosts {
  id: string;
  company: string;
  jobs: JobPost[];
}

export interface JobPost {
  url: string;
  content: string;
  hash: number;
  page: any;
  realjobposturl: string;
  subUrl: string;
  // optional fields if you have them in JSON
  title?: string;
  location?: string;
  type?: string;
  remote?: boolean;
  tags?: string[];
}

export interface FlatJob {
  id: string;
  title: string;
  company: string;
  location: string;
  type: string;
  remote: boolean;
  tags: string[];
  url: string | null;
  content: string;
}

const Jobs = () => {
  const {
    searchQuery, setSearchQuery,
    locationFilter, setLocationFilter,
    typeFilter, setTypeFilter,
    remoteFilter, setRemoteFilter,
    scrollY, setScrollY,
    seenJobs
  } = useJobsContext();

  const location = useLocation();
  const navigate = useNavigate();

  const [uniqueLocations, setUniqueLocations] = useState<string[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<FlatJob | null>(null);
  const [filteredJobs, setFilteredJobs] = useState<FlatJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [skills, setSkills] = useState<string[] | null>(null);

  // nested data from JSON
  const companiesWithJobs: CompanyWithJobPosts[] = (jobsData?.data || []) as CompanyWithJobPosts[];
  const BLOCKED_DOMAINS = ['welcometothejungle', 'indeed', 'myworkdayjobs'];
  // flatten companiesWithJobs -> FlatJob[]
  const flatJobs: FlatJob[] = companiesWithJobs.flatMap((company) =>
    (company.jobs || [])
      .filter(
        job => !BLOCKED_DOMAINS.some(domain => job.url.includes(domain))
      )
      .map((job, idx) => {
        const url =
          job.realjobposturl ||
          job.url ||
          job.subUrl ||
          extractUrl(job.content || '');

        return {
          id: `${company.id}-${idx}`,
          title: job.title || company.company,
          company: company.company,
          location: job.location || '',
          type: job.type || '',
          remote: Boolean(job.remote),
          tags: job.tags,
          url,
          content: job.content
        };
      }
    )
  );
  

  // sync ?search= in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (searchQuery) {
      params.set('search', searchQuery);
    } else {
      params.delete('search');
    }
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  }, [searchQuery, navigate, location.pathname]);

  // compute unique locations + restore scroll
  useEffect(() => {
    const locations = Array.from(
      new Set(flatJobs.map(job => job.location).filter(loc => !!loc))
    );
    setUniqueLocations(locations);

    const timeout = setTimeout(() => {
      window.scrollTo(0, scrollY);
    }, 50);

    return () => clearTimeout(timeout);
  }, [scrollY, flatJobs]);

  // scroll to top when filters sidebar opens on mobile
  useEffect(() => {
    if (filtersOpen) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [filtersOpen]);

  // persist scroll on leave
  useEffect(() => {
    return () => {
      setScrollY(window.scrollY);
    };
  }, [setScrollY]);

  // stop bell shaking animation after delay
  useEffect(() => {
    const icon = document.getElementById('stop-shaking-after-delay');
    if (!icon) return;

    const timeout = setTimeout(() => {
      icon.classList.remove('fa-shake');
    }, 3000);

    return () => clearTimeout(timeout);
  }, []);

  // filtering
  useEffect(() => {
    setLoading(true);

    const searchTerms = (searchQuery || '')
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);

    const filtered: FlatJob[] = flatJobs
      .filter(job => {
        const matchesSearch =
          searchTerms.length === 0 ||
          searchTerms.some(term => {
            const t = term.toLowerCase();
            const inTitle = job.title.toLowerCase().includes(t);
            const inCompany = job.company.toLowerCase().includes(t);
            const inTags = job.tags.some(tag => tag.toLowerCase().includes(t));
            return inTitle || inCompany || inTags;
          });

        const matchesLocation =
          locationFilter === '' || job.location === locationFilter;

        const matchesType =
          typeFilter === '' || job.type === typeFilter;

        const matchesRemote =
          remoteFilter === '' ||
          (remoteFilter === 'remote' && job.remote) ||
          (remoteFilter === 'onsite' && !job.remote);

        return matchesSearch && matchesLocation && matchesType && matchesRemote;
      });

    setFilteredJobs(filtered);
    setLoading(false);

    ReactGA.event({
      category: 'search-action',
      action: 'filter_updated-jobs',
      label: 'Search/Filter Action',
      value: filtered.length,
      params: {
        search: searchQuery,
        location: locationFilter,
        type: typeFilter,
        remote: remoteFilter,
        resultsCount: filtered.length
      }
    });
  }, [searchQuery, locationFilter, typeFilter, remoteFilter, flatJobs]);



  function extractUrl(text: string): string | null {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const match = text.match(urlRegex);
    return match ? match[0] : null;
  }


  return (
    <>
      <SEO
        title="Tech Jobs in Sophia Antipolis & French Riviera | PACA-Dev"
        description="Browse the latest tech job opportunities in Sophia Antipolis, Nice, Cannes, and Monaco. Find developer, data science, and tech management positions."
        keywords="sophia antipolis jobs, tech jobs nice, developer jobs cannes, remote jobs france"
      />

      <div className="min-h-screen bg-gray-50">

        {/* Header */}
        <div className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-2 lg:px-8 py-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {skills && skills.length > 0
                ? 'Skills from Your CV'
                : filteredJobs.length > 0
                ? `Found ${filteredJobs.length} job posts`
                : 'Tech Jobs'}
            </h1>

            {(searchQuery || '').trim() !== '' && (
              <div className="w-full mb-6">
                <div className="flex flex-wrap items-center gap-2">
                  {(searchQuery || '').split(/\s+/).map((word, index) => (
                    <div
                      key={index}
                      className="flex items-center bg-blue-100 text-blue-800 rounded-full px-3 py-1 text-sm font-medium"
                    >
                      <span>{word}</span>
                      <button
                        onClick={() => {
                          const words = (searchQuery || '').split(/\s+/);
                          words.splice(index, 1);
                          setSearchQuery(words.join(' '));
                        }}
                        className="ml-2 focus:outline-none"
                        aria-label={`Remove filter ${word}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* <p className="mt-1">
              <button
                onClick={() =>
                  document.getElementById('subscribe')?.scrollIntoView({ behavior: 'smooth' })
                }
                className="inline-flex gap-2 transition"
              >
                Get alerts on new job posts
                <div className="fa-sm mt-1">
                  <i id="stop-shaking-after-delay" className="fa-solid fa-bell fa-shake" />
                </div>
              </button>
            </p> */}

            {skills && skills.length > 0 && <SkillsList skills={skills} />}
          </div>
        </div>

        {/* Floating filter toggle button bottom-right */}
        <div className="fixed bottom-5 right-5 p-3 rounded-full bg-white shadow-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-center text-gray-600 lg:hidden z-50">
          <FilterToggleButton isOpen={filtersOpen} onToggle={() => setFiltersOpen(!filtersOpen)} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Filters Sidebar */}
            <div
              id="filters-sidebar"
              className={`bg-white rounded-lg shadow-sm p-6 top-24 lg:block ${
                filtersOpen ? 'block' : 'hidden'
              } lg:w-1/4`}
              style={{ maxHeight: 'calc(100vh - 6rem)', overflowY: 'auto' }}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Filter className="h-5 w-5 mr-2" />
                Filters
              </h3>

              {/* Search */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Job title, company, skills, location..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                </div>
              </div>

              {/* Location */}
              {/* <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <select
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Locations</option>
                  {uniqueLocations.map((loc, index) => (
                    <option key={index} value={loc}>
                      {loc}
                    </option>
                  ))}
                </select>
              </div> */}

              {/* Job Type */}
              {/* <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Job Type
                </label>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Types</option>
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Contract">Contract</option>
                  <option value="Internship">Internship</option>
                </select>
              </div> */}

              {/* Remote */}
              {/* <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Work Style
                </label>
                <select
                  value={remoteFilter}
                  onChange={(e) => setRemoteFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All</option>
                  <option value="remote">Remote OK</option>
                  <option value="onsite">On-site Only</option>
                </select>
              </div> */}

              <button
                onClick={() => {
                  setSearchQuery('');
                  setLocationFilter('');
                  setTypeFilter('');
                  setRemoteFilter('');
                }}
                className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors"
              >
                Clear All Filters
              </button>
            </div>

            {/* Jobs List – all posts from nested jobsData */}
            <div className="lg:w-3/4">
              <div className="mb-6 flex justify-between items-center">
                <p className="text-gray-600">
                  Showing {filteredJobs.length} job posts
                </p>
              </div>

              <div className="grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-2">
                {filteredJobs.map((job) => {
                  const isSeen = seenJobs.has(job.id);
                  
                  return (
                    <div
                      key={job.id}
                      className={`border rounded-xl p-2 transition ${
                        isSeen ? 'opacity-70' : 'opacity-100'
                      }`}
                    >
                      <div className="rounded-lg p-2 mb-2 bg-gray-100">
                        
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between md:mb-4 sm:mb-2 md:space-y-4 sm:space-y-0 sm:space-x-6">
                          
                          <div className="flex items-center space-x-4 flex-shrink-0">
                            <CompanyLogo companyName={job.company}></CompanyLogo>
                          </div>

                          <div className="flex-1 min-w-0">
                            {job.title && <>
                              <h3
                                className="text-lg sm:text-xl font-semibold text-gray-900 mb-1 break-words"
                              >
                                {job.title}
                              </h3>
                            </>}
                            <p className="text-gray-700 text-bold">
                              {job.company}
                              {job.location && ` · ${job.location}`}
                            </p>
                          </div>

                        </div>

                        <p className="text-gray-700 mb-4 line-clamp-3 sm:line-clamp-5">
                          {job.tags.map(tag => (
                            <span
                              key={tag}
                              className="inline-flex bg-gray-200 px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700"
                            >
                              {tag}
                            </span>
                          ))}
                        </p>

                      </div>

                      <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
                        {job.url && (
                          <a
                            href={job.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full sm:w-auto"
                          >
                            <button className="bg-black rounded-full text-white px-4 py-1 sm:w-auto text-center">
                              Details
                            </button>
                          </a>
                        )}

                        {/* {companyMeta?.website && companyMeta.website.length > 1 && (
                          <a
                            href={`https://${companyMeta.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full sm:w-auto"
                          >
                            <button className="bg-gray-100 text-gray-800 px-5 py-2 rounded-md hover:bg-gray-200 transition-colors w-full sm:w-auto text-center">
                              Visit company site
                            </button>
                          </a>
                        )} */}
                      </div>
                    </div>
                  );
                })}
              </div>

              {filteredJobs.length === 0 && (
                <div className="text-center py-12">
                  <Briefcase className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  {loading ? (
                    <>
                      <div className="text-center p-10">
                        <AnimatedHand />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Loading</h3>
                    </>
                  ) : (
                    <>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">No jobs found</h3>
                    </>
                  )}
                  <p className="text-gray-600">
                    Try adjusting your search criteria or check back later for new opportunities.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Newsletter */}
        <div id="subscribe" />
        <NewsletterSubscription />
      </div>
    </>
  );
};

export default Jobs;